import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Backdrop,
  Box,
  Button,
  Chip,
  CircularProgress,
  CssBaseline,
  Grid,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PublishIcon from "@mui/icons-material/Publish";
import { Chip as MuiChip } from "@mui/material";

import GeneralAdminSidebar from "../../../../../common/GeneralAdminSidebar";
import DetailsPageHeader, {
  headerPrimaryButtonSx,
  headerSecondaryButtonSx,
} from "../../../../../common/DetailsPageHeader";
import {
  GeneralAdminRoutes,
  SALES_SECTION_REGISTRY,
  mainLayoutStyles,
  normalizeSalesDisplayOn,
  showNotification,
} from "../../../../../constants";
import { useAuth } from "../../../../../hooks";
import {
  createSalesSection,
  deleteSalesSection,
  getSalesPageSections,
  getSalesPageById,
  getPublishedSalesPageById,
  getPublishedSectionsMap,
  isPublishPending,
  publishPage,
  publishSection,
  savePageToStaging,
  toggleSectionEnabled,
} from "../../hooks/SalesPagesHook";

/**
 * GeneralAdmin > QuickTales.com > Pages > (page) content builder.
 * Lists the section documents for a marketing page (identified by its `:slug`
 * route param) and lets the admin add / edit / delete / enable / disable them.
 * See docs/SALES_PAGES_CMS.md
 */
const SalesPagesContainer = () => {
  const navigate = useNavigate();
  const { pageId } = useParams();
  const { currentUser } = useAuth();
  const userId = currentUser?.id;

  const [loading, setLoading] = useState(false);
  const [sections, setSections] = useState([]);
  const [pageTitle, setPageTitle] = useState("");
  const [page, setPage] = useState(null);
  const [publishedPage, setPublishedPage] = useState(null);
  const [publishedSections, setPublishedSections] = useState({});

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId]);

  const loadAll = async () => {
    try {
      setLoading(true);
      const [draftPage, pubPage, draftSections, pubSections] =
        await Promise.all([
          getSalesPageById(pageId),
          getPublishedSalesPageById(pageId),
          getSalesPageSections(pageId),
          getPublishedSectionsMap(pageId),
        ]);
      setPage(draftPage);
      setPageTitle(draftPage?.title || pageId);
      setPublishedPage(pubPage);
      setSections(draftSections);
      setPublishedSections(pubSections);
    } catch (error) {
      console.log("Error loading page content:", error.message);
    } finally {
      setLoading(false);
    }
  };

  // The page-level Publish is available when the page draft itself is ahead OR
  // any of its sections are ahead of their published twins.
  const pagePublishPending =
    isPublishPending(page, publishedPage) ||
    sections.some((s) => isPublishPending(s, publishedSections[s.id]));

  const handleToggle = async (section) => {
    try {
      const next = !section.enabled;
      setSections((prev) =>
        prev.map((s) => (s.id === section.id ? { ...s, enabled: next } : s))
      );
      await toggleSectionEnabled(section.id, next, userId);
    } catch (error) {
      console.log("Error toggling section:", error.message);
      showNotification("Error updating section");
      loadAll();
    }
  };

  const handleDelete = async (section) => {
    const label =
      section.title || SALES_SECTION_REGISTRY[section.section]?.label;
    if (!window.confirm(`Delete "${label}"? This cannot be undone.`)) return;
    try {
      setLoading(true);
      await deleteSalesSection(section.id);
      showNotification("Section deleted");
      await loadAll();
    } catch (error) {
      console.log("Error deleting section:", error.message);
      showNotification("Error deleting section");
      setLoading(false);
    }
  };

  const handleDuplicate = async (section) => {
    try {
      setLoading(true);
      // Copy all fields except the document id; append " - Copy" to the title
      // and place the copy just after the original in order.
      const { id, ...rest } = section;
      const payload = {
        ...rest,
        title: `${section.title || ""} - Copy`.trim(),
        order: (section.order ?? 0) + 1,
      };
      await createSalesSection(payload, userId);
      showNotification("Section duplicated");
      await loadAll();
    } catch (error) {
      console.log("Error duplicating section:", error.message);
      showNotification("Error duplicating section");
      setLoading(false);
    }
  };

  const handlePublishSection = async (section) => {
    try {
      setLoading(true);
      await publishSection(section.id, userId);
      showNotification("Section published");
      await loadAll();
    } catch (error) {
      console.log("Error publishing section:", error.message);
      showNotification("Error publishing section");
      setLoading(false);
    }
  };

  const handlePublishPage = async () => {
    try {
      setLoading(true);
      await publishPage(pageId, userId);
      showNotification("Page published");
      await loadAll();
    } catch (error) {
      console.log("Error publishing page:", error.message);
      showNotification("Error publishing page");
      setLoading(false);
    }
  };

  // Save = trigger the staging.quicktales.com redeploy for this page's drafts.
  const handleSavePage = async () => {
    try {
      setLoading(true);
      await savePageToStaging(pageId, userId);
      showNotification("Page saved — staging redeploying");
    } catch (error) {
      console.log("Error saving page:", error.message);
      showNotification("Error saving page");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () =>
    navigate(`/sales-pages/content/${pageId}/add`);

  const handleEdit = (section) =>
    navigate(`/sales-pages/content/${pageId}/edit/${section.id}`);

  const handleBack = () => navigate(GeneralAdminRoutes.salesPagesList);

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <DetailsPageHeader
        mainTitle={`QuickTales.com — ${pageTitle}`}
        bgColor="#fff"
        onBackClick={handleBack}
        actions={
          <>
            <Button
              variant="contained"
              sx={headerPrimaryButtonSx}
              onClick={handleSavePage}
              disabled={loading || !page}
            >
              Save
            </Button>
            {pagePublishPending && (
              <Button
                variant="contained"
                sx={headerPrimaryButtonSx}
                onClick={handlePublishPage}
                disabled={loading}
              >
                Publish Page
              </Button>
            )}
            <Button
              variant="outlined"
              sx={headerSecondaryButtonSx}
              onClick={handleAdd}
            >
              Add Section
            </Button>
          </>
        }
      />
      <GeneralAdminSidebar />
      <Grid container style={{ ...mainLayoutStyles, background: "#fff" }}>
        <Grid
          container
          direction="column"
          style={{ padding: 30, marginTop: 40 }}
        >
          <Paper sx={{ p: 3 }}>
            {sections.length === 0 && !loading ? (
              <Typography sx={emptyStyles}>
                No sections yet for this page. Click "Add Section" to create one.
              </Typography>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellStyles}>Order</TableCell>
                    <TableCell sx={headCellStyles}>Type</TableCell>
                    <TableCell sx={headCellStyles}>Title</TableCell>
                    <TableCell sx={headCellStyles}>Display On</TableCell>
                    <TableCell sx={headCellStyles}>Enabled</TableCell>
                    <TableCell sx={headCellStyles}>Status</TableCell>
                    <TableCell sx={headCellStyles} align="right">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sections.map((section) => {
                    const pending = isPublishPending(
                      section,
                      publishedSections[section.id]
                    );
                    return (
                    <TableRow key={section.id} hover>
                      <TableCell sx={cellStyles}>{section.order}</TableCell>
                      <TableCell sx={cellStyles}>
                        <Chip
                          size="small"
                          label={
                            SALES_SECTION_REGISTRY[section.section]?.label ||
                            section.section
                          }
                        />
                      </TableCell>
                      <TableCell sx={cellStyles}>
                        {section.title || "—"}
                      </TableCell>
                      <TableCell sx={cellStyles}>
                        {(() => {
                          const d = normalizeSalesDisplayOn(section.displayOn);
                          if (d === "both") return "Both";
                          return (
                            <Chip
                              size="small"
                              variant="outlined"
                              color={d === "desktop" ? "primary" : "secondary"}
                              label={d === "desktop" ? "Desktop" : "Mobile"}
                            />
                          );
                        })()}
                      </TableCell>
                      <TableCell sx={cellStyles}>
                        <Switch
                          checked={!!section.enabled}
                          onChange={() => handleToggle(section)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={cellStyles}>
                        {pending ? (
                          <MuiChip
                            size="small"
                            color="warning"
                            label={
                              publishedSections[section.id]
                                ? "Unpublished changes"
                                : "Not published"
                            }
                          />
                        ) : (
                          <MuiChip
                            size="small"
                            color="success"
                            variant="outlined"
                            label="Published"
                          />
                        )}
                      </TableCell>
                      <TableCell sx={cellStyles} align="right">
                        <IconButton
                          size="small"
                          onClick={() => handlePublishSection(section)}
                          title="Publish"
                          disabled={!pending}
                          sx={{ color: pending ? "#2E7D32" : undefined }}
                        >
                          <PublishIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(section)}
                          title="Edit"
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDuplicate(section)}
                          title="Duplicate"
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(section)}
                          sx={{ color: "#B00020" }}
                          title="Delete"
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default SalesPagesContainer;

const headCellStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontWeight: 700,
  fontSize: 13,
  color: "#020303",
};

const cellStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontSize: 14,
  color: "#020303",
};

const emptyStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontSize: 14,
  color: "#808080",
  p: 2,
};
